/**
 * Persists the map viewport (center, zoom, pitch, bearing) to sessionStorage
 * on page unload and restores it on the next mount.
 *
 * Uses sessionStorage (not localStorage or cookies) so the state is only
 * restored on a same-tab refresh — opening a new tab starts fresh.
 *
 * The flight selection is already persisted via the omen_tracker_selection
 * cookie; this module only handles the camera state.
 */

import type { MapRef } from 'react-map-gl/maplibre';

const SESSION_KEY = 'omen_session';

export interface MapViewport {
  /** [longitude, latitude] */
  center: [number, number];
  zoom: number;
  pitch: number;
  bearing: number;
}

/**
 * Saves the current map camera state to sessionStorage.
 * Call this inside a `window.beforeunload` handler.
 * @param map - the react-map-gl MapRef instance
 */
export function saveViewport(map: MapRef): void {
  try {
    const c = map.getCenter();
    const data: MapViewport = {
      center:  [c.lng, c.lat],
      zoom:    map.getZoom(),
      pitch:   map.getPitch(),
      bearing: map.getBearing(),
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch {
    // sessionStorage may throw in private mode with storage quota exceeded
  }
}

/**
 * Reads and consumes the saved viewport from sessionStorage.
 * Returns `null` if nothing is saved or the stored data is malformed.
 * Removes the entry after reading so subsequent mounts get the default.
 */
export function loadAndConsumeViewport(): MapViewport | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(SESSION_KEY);
    return JSON.parse(raw) as MapViewport;
  } catch {
    return null;
  }
}
